import { getCarriedQueryParams } from "../utils/urlParamsAndFilters";
import { useRouter } from "next/router";
import { toast } from "react-hot-toast";
import EntityCard from "./EntityCard";
import {
  GlobeIcon,
  MoneysIcon,
  PlusCircleIcon,
  SecurityCardIcon,
} from "icons/outline";
import Link from "next/link";
import { usePlatformConnect } from "../contexts/PlatformConnectProvider";

export default function ProviderCard({
  provider,
  isAboveActiveProviderLimit = false,
  currency,
}) {
  const {
    formID,
    countries,
    payName,
    payinMethods,
    payoutMethods,
    type,
    icon,
  } = provider;
  const router = useRouter();
  const { isPlatformConnectMode } = usePlatformConnect();

  const allMethodsLength = [...payinMethods, ...payoutMethods].length;

  const getProviderPrimaryAction = () => {
    return isAboveActiveProviderLimit ? (
      <EntityCard.PrimaryButton
        onClick={() =>
          toast.error(
            "You reached the maximum limit for the active providers!",
            {
              id: "provider-limit-reached",
            }
          )
        }
      >
        Connect
      </EntityCard.PrimaryButton>
    ) : (
      <EntityCard.PrimaryButton
        render={
          <Link
            href={{
              pathname: `/connections/${formID}/integration/`,
              query: {
                ...getCarriedQueryParams(router.query),
                // override currency to use modal filters if not in platform connect mode
                ...(!isPlatformConnectMode && { currency }),
                type: "available",
              },
            }}
          />
        }
      >
        Connect
      </EntityCard.PrimaryButton>
    );
  };

  const getProviderSecondaryAction = () => {
    return (
      <EntityCard.SecondaryButton
        render={
          <Link
            href={{
              pathname: `/connections/${formID}/`,
              query: {
                ...getCarriedQueryParams(router.query),
                type: "available",
              },
            }}
          />
        }
      >
        Learn more
      </EntityCard.SecondaryButton>
    );
  };

  const getExtraProviderInfo = () => {
    if (countries.length > 0)
      return (
        <div className="flex items-center gap-2">
          <GlobeIcon
            width={16}
            height={16}
            className="shrink-0 text-foundations-subtler"
          />

          <span className="line-clamp-1 text-ellipsis">
            {countries
              .slice(0, 2)
              .map((country) => country.country_name)
              .join(", ")}

            {countries.length > 2 && ` and ${countries.length - 2} more`}
          </span>
        </div>
      );

    if (type === "Risk & Fraud")
      return (
        <div className="flex items-center gap-2">
          <SecurityCardIcon
            width={16}
            height={16}
            className="shrink-0 text-foundations-subtler"
          />

          <span className="line-clamp-1 text-ellipsis">Risk service</span>
        </div>
      );

    if (type === "Additional Services")
      return (
        <div className="flex items-center gap-2">
          <PlusCircleIcon
            width={16}
            height={16}
            className="shrink-0 text-foundations-subtler"
          />

          <span className="line-clamp-1 text-ellipsis">Additional service</span>
        </div>
      );
  };

  return (
    <EntityCard
      imageDimensions={56}
      imageUrl={icon}
      subTitle={type}
      title={payName}
      primaryAction={getProviderPrimaryAction()}
      secondaryAction={getProviderSecondaryAction()}
    >
      <div className="flex items-center gap-2 text-sm font-medium text-foundations-subtle md:py-[7px]">
        {!!allMethodsLength && (
          <div className="flex items-center gap-2">
            <MoneysIcon
              width={16}
              height={16}
              className="shrink-0 text-foundations-subtler"
            />

            <span className="whitespace-nowrap">
              {allMethodsLength} {`method${allMethodsLength > 1 ? "s" : ""}`}
              {countries.length > 0 && ", "}
            </span>
          </div>
        )}

        {getExtraProviderInfo()}
      </div>
    </EntityCard>
  );
}
